<template>
  <form-block
    :title="title !== null ? title : tr('Geotargeting')"
    :description="description"
  >
    <p v-if="targetAudienceBlockIsLoading" class="m-0 flex items-center gap-2 text-muted-foreground">
      <loader-circle-icon class="size-4 animate-spin" />
      {{ tr('Loading...') }}
    </p>

    <template v-else>
      <geolocation-target-selector
        v-if="geoAvailable"
        ref="geolocationTargetSelector"
        v-model="entity.settings.adset_targeting"
        :disabled="disabled"
      />
      <info-text v-if="geoMessage" :type="geoMessageType || 'info'">
        {{ geoMessage }}
      </info-text>

      <div class="flex flex-col gap-1.5">
        <ui-label>
          {{ tr('Spoken languages') }}
          <span class="font-normal text-muted-foreground">({{ tr('optional') }})</span>
        </ui-label>
        <multi-select
          v-if="languageAvailable"
          v-model="entity.settings.adset_targeting.locales"
          :options="languages"
          value-key="id"
          label-key="name"
          :placeholder="tr('Select languages...')"
          :disabled="disabled"
        />
        <info-text v-else-if="languageMessage" :type="languageMessageType || 'info'">
          {{ languageMessage }}
        </info-text>
      </div>
    </template>
  </form-block>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import GeolocationTargetSelector from '@/ui/components/GeolocationTargetSelector.vue';
import InfoText from '@/ui/components/InfoText.vue';
import MultiSelect from '@/ui/components/MultiSelect.vue';

export default {
  name: 'GeotargetBlock',
  components: {
    GeolocationTargetSelector,
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    MultiSelect,
  },
  mixins: [ RuleFormBlockMixin ],
  props: {
    title: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    targetAudienceBlockIsLoading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    geoAvailable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    geoMessage: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    geoMessageType: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    languageAvailable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    languageMessage: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    languageMessageType: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
  },
  computed: {
    description() {
      if (!this.geoAvailable) {
        return null;
      }

      return this.tr('You have to specify where the ad should be displayed, or you have to choose a target audience where geographic targeting is set.');
    },
    languages() {
      const list = JSON.parse(JSON.stringify(this.$store.getters['app/facebookLanguages']));
      list.forEach(item => item.name = this.tr(item.name));
      list.sort((a, b) => a.name.localeCompare(b.name));

      return list;
    },
  },
  methods: {
    validate() {
      if (!this.geoAvailable) {
        return true;
      }

      if (
        this.entity.settings.adset_included_custom_audiences.length === 0
        && this.entity.settings.adset_targeting.geo_locations.all.length === 0
      ) {
        this.error(this.tr('Please add a location for your target audience or use a Custom Audience.'));
        return false;
      }

      if (this.$refs['geolocationTargetSelector'] && !this.$refs['geolocationTargetSelector'].validate()) {
        return false;
      }

      return true;
    },
  },
};
</script>
